import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Details,
  Search,
  TableHeader,
  AboutNode,
  InstallationsSummary,
} from '@/components'
import { addDealPath, makeSearchPath, print, proposalPath, sort } from '@/utils'
import { OrderType } from '@/types'
import { t } from '@/i18n'

import EditProposal from './components/editProposal'
import ProposalRow from './components/proposalRow'
import { useSelect } from './useSelect'
import { useBuildData } from './utils'
import { useData } from './useData'

const columns = [
  {
    key: 'id',
    sortKey: 'id',
  },
  {
    key: 'car_model',
  },
  {
    key: 'car_brand',
  },
]

const Proposals: React.FC = () => {
  const { id, customerId } = useParams()
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)
  const [sortKey, setKey] = useState<string>()
  const [orderBy, setOrderBy] = useState<OrderType>()
  const _customerId = useMemo(
    () => (customerId === 'customer' ? undefined : Number(customerId)),
    [customerId],
  )
  const { data, loading, onSearch, onFetchMore, pageInfo } =
    useSelect(_customerId)
  const {
    data: selectedData,
    onDelete,
    loading: proposalLoading,
  } = useData({ id })

  const onClose = useCallback(() => {
    navigate(makeSearchPath(proposalPath, { customerId: _customerId }))
  }, [navigate, _customerId])

  const HeaderComponent = useMemo(
    () => <Search onSearch={onSearch} />,
    [onSearch],
  )

  const details = useBuildData(selectedData)

  const sortingBy = useCallback((key?: string, by?: OrderType) => {
    setKey(key)
    setOrderBy(by)
  }, [])

  return (
    <>
      <div className="row">
        <div className={`col w-${!id ? '100' : '45'}`}>
          <Details
            showNoData
            data={data.sort((a, b) => sort(a, b, sortKey, orderBy))}
            Wrapper="table"
            RowParent="tbody"
            Row={ProposalRow}
            TitleWrapper="h4"
            loading={loading}
            title={t('proposal')}
            onFetchMore={onFetchMore}
            listWrapperClassName="list-items"
            wrapperClassName="info-box w-100"
            HeaderComponent={HeaderComponent}
            dataBoxWrapperClassName="info-table"
            fetchMoreStatus={pageInfo.fetchMoreStatus}
            Header={() => (
              <TableHeader
                columns={columns}
                onSort={sortingBy}
                orderedBy={orderBy}
              />
            )}
          />
        </div>
        <div className="col w-55">
          <AboutNode
            loading={proposalLoading}
            sections={[
              {
                title: 'general_info',
                data: details.general_info,
                headerButtons: [
                  {
                    children: (
                      <>
                        <span className="icon-delete"></span>
                        {t('delete')}
                      </>
                    ),
                    className: 'main-btn red',
                    onClick: () => {
                      onDelete(onClose)
                    },
                  },
                  {
                    children: (
                      <>
                        <span className="icon-edit"></span>
                        {t('edit')}
                      </>
                    ),
                    className: 'main-btn outline',
                    onClick: () => setVisible(true),
                  },
                  {
                    className: 'main-btn',
                    children: t('create_deal_from_proposal'),
                    onClick: () =>
                      navigate(`${addDealPath}?proposal=${id || ''}`),
                  },
                  {
                    children: (
                      <>
                        <span className="icon-print"></span>
                        {t('print')}
                      </>
                    ),
                    className: 'main-btn light',
                    onClick: () => print('proposal', selectedData?.id),
                  },
                ],
              },
              {
                title: 'financial_details',
                data: details.financial_details,
              },
              {
                title: 'installations',
                customContent: selectedData ? (
                  <InstallationsSummary
                    installations={
                      // Handle both transformed array and raw GraphQL structure
                      Array.isArray(selectedData?.installations)
                        ? selectedData.installations
                        : ((selectedData?.installations?.edges
                            ?.map(edge => edge?.node)
                            .filter(Boolean) || []) as any[])
                    }
                    isEditing={false}
                    showManageButton={false}
                  />
                ) : null,
              },
            ]}
            node={visible || !id ? undefined : selectedData}
            onClose={onClose}
          />

          <EditProposal
            visible={visible}
            node={selectedData}
            onClose={() => setVisible(false)}
          />
        </div>
      </div>
    </>
  )
}

export default Proposals
